/**
 * チバリヨ S2 日報タイムライン (Phase 2-B-2)
 *
 * Phase 2-B-3.3 改修:
 *   - 自分の日報 (admin は全員分) に「⋯」メニュー → 編集 / 削除
 *   - 編集はシェルの onEditReport コールバック経由で S3 編集モードを開く
 *   - 削除は likes / comments サブコレクション全件 + 本体を writeBatch で連鎖削除
 *   - 「編集済み」ラベル (updatedAt - createdAt > 2秒 で判定)
 *
 * 機能:
 *   - 全社1本のフィード (日付グループ + 日内 time 降順)
 *   - 初回20件 + 「もっと見る」で20件ずつ追加 (startAfter カーソル)
 *   - カード: アバター / 名前 / time / 顧客 / 種別バッジ / アポ・見積チップ / act 2行クランプ
 *   - 展開: act 全文 + エリア / 面会者 / 所感
 *   - いいね: likes サブコレ (docId = memberId) + likeCount increment (writeBatch)
 *   - コメント: インライン展開 + 入力欄、comments サブコレ + commentCount increment
 *   - コメントの編集/削除: 本人のみ (updateDoc で text/updatedAt 更新、削除は
 *     writeBatch でコメント削除 + commentCount decrement)
 *   - 未読バッジ用ヘルパー: getS2UnreadCount / markS2Read (localStorage 既読管理)
 *
 * クエリ設計の注意:
 *   - orderBy('time') は使わない (旧 seed データに time フィールドが無く、
 *     orderBy 対象フィールドの無い doc は Firestore クエリから除外されるため)
 *   - orderBy('date', 'desc'), orderBy('createdAt', 'desc') で取得し、
 *     日付グループ内の表示順はクライアント側で (time || '') 降順ソート
 *   - 複合インデックス reports(date DESC, createdAt DESC) が必要 (Phase 2-B-2 で追加)
 */

import { db } from './firebase-init.js';
import {
  collection,
  doc,
  query,
  orderBy,
  where,
  limit,
  startAfter,
  getDocs,
  getDoc,
  getCountFromServer,
  writeBatch,
  updateDoc,
  increment,
  serverTimestamp,
  Timestamp,
} from 'https://www.gstatic.com/firebasejs/10.13.0/firebase-firestore.js';

const PAGE_SIZE = 20;

const AVATAR_COLORS = {
  blue:   { text: '#0C447C', bg: '#E6F1FB' },
  teal:   { text: '#0F6E56', bg: '#E1F5EE' },
  amber:  { text: '#854F0B', bg: '#FAEEDA' },
  purple: { text: '#3C3489', bg: '#EEEDFE' },
  coral:  { text: '#A32D2D', bg: '#FCEBEB' },
  slate:  { text: '#475569', bg: '#F1F5F9' },
  rose:   { text: '#9F1239', bg: '#FFE4E6' },
  indigo: { text: '#3730A3', bg: '#E0E7FF' },
};

// ============================================================
// 未読バッジ ヘルパー (シェルから利用)
// ============================================================

function lastSeenKey(memberId) {
  return `chibariyo_s2_lastSeen_${memberId}`;
}

/** S2 を既読化 (lastSeen = 現在時刻) */
export function markS2Read(memberId) {
  try {
    localStorage.setItem(lastSeenKey(memberId), String(Date.now()));
  } catch (e) { /* private mode 等は無視 */ }
}

/**
 * 未読件数を取得 (createdAt > lastSeen の件数、自分の投稿も含む)
 * 初回 (lastSeen 未保存) は現在時刻をセットして 0 を返す
 */
export async function getS2UnreadCount(memberId) {
  let raw = null;
  try {
    raw = localStorage.getItem(lastSeenKey(memberId));
  } catch (e) { /* noop */ }

  if (!raw) {
    markS2Read(memberId);
    return 0;
  }

  const ts = Timestamp.fromMillis(Number(raw));
  const q = query(collection(db, 'reports'), where('createdAt', '>', ts));
  const snap = await getCountFromServer(q);
  return snap.data().count;
}

// ============================================================
// ユーティリティ
// ============================================================

function esc(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const YOUBI = ['日', '月', '火', '水', '木', '金', '土'];

function toDateOnlyStr(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** "YYYY-MM-DD" -> 「今日 6/11(木)」「昨日 6/10(水)」「6月9日(火)」「2025年12月28日(日)」 */
function formatDateGroupLabel(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return dateStr;
  const dt = new Date(y, m - 1, d);
  const youbi = YOUBI[dt.getDay()];

  const now = new Date();
  const todayStr = toDateOnlyStr(now);
  const yest = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  const yestStr = toDateOnlyStr(yest);

  if (dateStr === todayStr) return `今日 ${m}/${d}(${youbi})`;
  if (dateStr === yestStr) return `昨日 ${m}/${d}(${youbi})`;
  if (y !== now.getFullYear()) return `${y}年${m}月${d}日(${youbi})`;
  return `${m}月${d}日(${youbi})`;
}

/** コメントの createdAt (Timestamp) -> 「14:20」(当日) /「6/10 14:20」(過去日) */
function formatCommentTime(ts) {
  if (!ts || typeof ts.toDate !== 'function') return '';
  const d = ts.toDate();
  const hm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  if (toDateOnlyStr(d) === toDateOnlyStr(new Date())) return hm;
  return `${d.getMonth() + 1}/${d.getDate()} ${hm}`;
}

function avatarHTML(name, colorKey, sizeClass) {
  const palette = AVATAR_COLORS[colorKey] || AVATAR_COLORS.slate;
  const initial = esc((name || '?').slice(0, 1));
  return `<div class="s2-avatar ${sizeClass || ''}" style="color:${palette.text};background-color:${palette.bg};">${initial}</div>`;
}

// ============================================================
// 本体
// ============================================================

/**
 * S2 タイムラインをマウント
 * @param {HTMLElement} rootEl
 * @param {Object} opts { currentMember, showToast }
 * @returns controller { destroy, onShow }
 */
export function mountS2Timeline(rootEl, opts = {}) {
  const currentMember = opts.currentMember;
  const showToast = opts.showToast || (() => {});
  const onEditReport = opts.onEditReport || null;
  // P7: スクロールで自動読込 (「もっと見る」ボタンを出さない)。未指定(モバイル)は従来どおりボタン。
  const autoLoad = !!opts.autoLoad;
  const myMemberId = currentMember.memberId;
  const isAdminUser = currentMember.role === 'admin';

  let destroyed = false;
  let loading = false;
  let lastDocSnap = null;      // ページングカーソル
  let hasMore = true;
  const itemMap = new Map();   // reportId -> item state
  let memberColorMap = null;   // memberId -> avatarColor

  rootEl.innerHTML = `
    <div class="s2-timeline">
      <div class="s2-feed" id="s2-feed"></div>
      <div class="s2-footer" id="s2-footer"></div>
    </div>
  `;

  const feedEl = rootEl.querySelector('#s2-feed');
  const footerEl = rootEl.querySelector('#s2-footer');

  // ---------- メンバー色マップ ----------

  async function loadMemberColors() {
    if (memberColorMap) return memberColorMap;
    memberColorMap = {};
    try {
      const snap = await getDocs(collection(db, 'members'));
      snap.forEach((d) => {
        memberColorMap[d.id] = d.data().avatarColor || 'slate';
      });
    } catch (e) {
      console.error('[S2] members 取得失敗:', e);
    }
    return memberColorMap;
  }

  // ---------- データ取得 ----------

  async function fetchPage() {
    const constraints = [
      orderBy('date', 'desc'),
      orderBy('createdAt', 'desc'),
      limit(PAGE_SIZE),
    ];
    if (lastDocSnap) constraints.push(startAfter(lastDocSnap));

    const q = query(collection(db, 'reports'), ...constraints);
    const snap = await getDocs(q);

    const items = snap.docs.map((d) => {
      const data = d.data();
      const cAt = data.createdAt;
      const uAt = data.updatedAt;
      const edited = !!(
        cAt && uAt &&
        typeof cAt.toMillis === 'function' && typeof uAt.toMillis === 'function' &&
        uAt.toMillis() - cAt.toMillis() > 2000
      );
      return {
        id: d.id,
        date: data.date || '',
        time: data.time || '',
        memberId: data.memberId || '',
        memberName: data.memberName || '',
        clientText: (data.client && data.client.text) || '',
        type: data.type || '',
        hasAppointment: !!data.hasAppointment,
        quote: !!data.quote,
        area: data.area || '',
        contact: data.contact || '',
        act: data.act || '',
        note: data.note || '',
        likeCount: data.likeCount || 0,
        commentCount: data.commentCount || 0,
        edited,
        likedByMe: false,
        commentsLoaded: false,
        likePending: false,
        deletePending: false,
      };
    });

    if (snap.docs.length > 0) lastDocSnap = snap.docs[snap.docs.length - 1];
    if (snap.docs.length < PAGE_SIZE) hasMore = false;

    // 自分の like 状態をまとめて取得
    await Promise.all(items.map(async (it) => {
      try {
        const likeSnap = await getDoc(doc(db, 'reports', it.id, 'likes', myMemberId));
        it.likedByMe = likeSnap.exists();
      } catch (e) { /* noop */ }
    }));

    return items;
  }

  async function loadNextPage() {
    if (loading || !hasMore || destroyed) return;
    loading = true;
    renderFooter('loading');
    try {
      await loadMemberColors();
      const items = await fetchPage();
      if (destroyed) return;
      items.forEach((it) => {
        if (itemMap.has(it.id)) return; // 重複ガード
        itemMap.set(it.id, it);
        insertCard(it);
      });
      if (itemMap.size === 0) {
        renderEmpty();
      } else {
        renderFooter(hasMore ? 'more' : 'end');
      }
    } catch (e) {
      console.error('[S2] フィード取得失敗:', e);
      renderFooter('error');
    } finally {
      loading = false;
      maybeAutoFill();
    }
  }

  async function resetAndLoad() {
    lastDocSnap = null;
    hasMore = true;
    itemMap.clear();
    feedEl.innerHTML = '';
    footerEl.innerHTML = '';
    await loadNextPage();
  }

  // 自動読込モード: 枠が埋まらない / 下までスクロールしたら次ページを読む
  function maybeAutoFill() {
    if (!autoLoad || destroyed || loading || !hasMore) return;
    // コンテンツが枠を満たしていない (= スクロールできない) 間は続けて読む
    if (rootEl.scrollHeight <= rootEl.clientHeight + 4) loadNextPage();
  }

  function onTimelineScroll() {
    if (!autoLoad || loading || !hasMore || destroyed) return;
    if (rootEl.scrollTop + rootEl.clientHeight >= rootEl.scrollHeight - 300) {
      loadNextPage();
    }
  }

  // ---------- レンダリング ----------

  function renderEmpty() {
    feedEl.innerHTML = `
      <div class="s2-empty">
        <p>まだ日報がありません</p>
        <p class="s2-empty-sub">下の + ボタンから最初の日報を書いてみましょう</p>
      </div>
    `;
    footerEl.innerHTML = '';
  }

  function renderFooter(state) {
    if (state === 'loading') {
      footerEl.innerHTML = `<div class="s2-loading">読み込み中…</div>`;
    } else if (state === 'more') {
      if (autoLoad) {
        footerEl.innerHTML = ''; // スクロールで自動読込するためボタンは出さない
      } else {
        footerEl.innerHTML = `<button type="button" class="s2-more-btn" id="s2-more-btn">もっと見る</button>`;
        footerEl.querySelector('#s2-more-btn').addEventListener('click', loadNextPage);
      }
    } else if (state === 'end') {
      footerEl.innerHTML = `<div class="s2-feed-end">ここまで</div>`;
    } else if (state === 'error') {
      footerEl.innerHTML = `
        <div class="s2-error">
          読み込みに失敗しました
          <button type="button" class="s2-retry-btn" id="s2-retry-btn">再試行</button>
        </div>
      `;
      footerEl.querySelector('#s2-retry-btn').addEventListener('click', loadNextPage);
    }
  }

  function getOrCreateDateGroup(dateStr) {
    let group = feedEl.querySelector(`.s2-date-group[data-date="${dateStr}"]`);
    if (group) return group.querySelector('.s2-date-group-body');

    group = document.createElement('div');
    group.className = 's2-date-group';
    group.dataset.date = dateStr;
    group.innerHTML = `
      <div class="s2-date-header">${esc(formatDateGroupLabel(dateStr))}</div>
      <div class="s2-date-group-body"></div>
    `;

    // 日付降順の位置に挿入
    const groups = Array.from(feedEl.querySelectorAll('.s2-date-group'));
    const before = groups.find((g) => g.dataset.date < dateStr);
    if (before) feedEl.insertBefore(group, before);
    else feedEl.appendChild(group);

    return group.querySelector('.s2-date-group-body');
  }

  function insertCard(it) {
    const body = getOrCreateDateGroup(it.date);
    const card = buildCard(it);

    // 日内 time 降順で挿入 (time 無し = '' は末尾に落ちる)
    const cards = Array.from(body.querySelectorAll('.s2-card'));
    const before = cards.find((c) => (c.dataset.time || '') < it.time);
    if (before) body.insertBefore(card, before);
    else body.appendChild(card);

    afterCardInserted(it, card);
  }

  function buildCard(it) {
    const colorKey = (memberColorMap && memberColorMap[it.memberId]) || 'slate';
    const typeClass = it.type === '訪問' ? 'is-visit' : 'is-quote-only';
    const canManage = it.memberId === myMemberId || isAdminUser;

    const chips = [];
    if (it.type === '訪問' && it.hasAppointment) chips.push('<span class="s2-chip s2-chip-appo">アポ</span>');
    if (it.quote) chips.push('<span class="s2-chip s2-chip-quote">見積</span>');

    const menuHTML = canManage ? `
        <div class="s2-card-menu-wrap">
          <button type="button" class="s2-card-menu-btn" data-action="menu" aria-label="メニュー">⋯</button>
          <div class="s2-card-menu">
            <button type="button" data-action="edit">編集</button>
            <button type="button" data-action="delete" class="is-danger">削除</button>
          </div>
        </div>` : '';

    const card = document.createElement('article');
    card.className = 's2-card';
    card.dataset.reportId = it.id;
    card.dataset.time = it.time;
    card.innerHTML = `
      <div class="s2-card-head">
        ${avatarHTML(it.memberName, colorKey)}
        <div class="s2-card-head-main">
          <div class="s2-card-name-row">
            <span class="s2-card-name">${esc(it.memberName)}</span>
            <span class="s2-card-time">${esc(it.time)}</span>
            ${it.edited ? '<span class="s2-edited-label">編集済み</span>' : ''}
          </div>
          <div class="s2-card-client-row">
            <span class="s2-card-client">${esc(it.clientText)}</span>
            <span class="s2-type-badge ${typeClass}">${esc(it.type)}</span>
            ${chips.join('')}
          </div>
        </div>
        ${menuHTML}
      </div>
      ${it.act ? `<p class="s2-card-act is-clamped">${esc(it.act)}</p>` : ''}
      <div class="s2-card-detail" style="display:none;"></div>
      <button type="button" class="s2-expand-btn" data-action="expand" style="display:none;">もっと見る</button>
      <div class="s2-card-foot">
        <button type="button" class="s2-like-btn ${it.likedByMe ? 'is-liked' : ''}" data-action="like" aria-label="いいね">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 21s-7.5-4.8-9.6-9C1 9 2.4 5.6 5.8 5.1c2-.3 3.8.8 4.7 2.4h3c.9-1.6 2.7-2.7 4.7-2.4 3.4.5 4.8 3.9 3.4 6.9-2.1 4.2-9.6 9-9.6 9z" transform="scale(0.92) translate(1,0)"/></svg>
          <span class="s2-like-count">${it.likeCount || ''}</span>
        </button>
        <button type="button" class="s2-comment-btn" data-action="comment" aria-label="コメント">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M21 12c0 4-4 7-9 7-1.2 0-2.3-.2-3.3-.5L4 20l1.4-3.4C4 15.3 3 13.8 3 12c0-4 4-7 9-7s9 3 9 7z"/></svg>
          <span class="s2-comment-count">${it.commentCount || ''}</span>
        </button>
      </div>
      <div class="s2-comments" style="display:none;">
        <div class="s2-comments-list"></div>
        <div class="s2-comment-input-row">
          <input type="text" class="s2-comment-input" placeholder="コメントを書く…" maxlength="500">
          <button type="button" class="s2-comment-send" data-action="send-comment">送信</button>
        </div>
      </div>
    `;
    return card;
  }

  /** カード挿入後: act のクランプ判定 → 展開ボタン表示制御 */
  function afterCardInserted(it, card) {
    const actEl = card.querySelector('.s2-card-act');
    const expandBtn = card.querySelector('.s2-expand-btn');
    const hasDetailFields = !!(it.area || it.contact || it.note);

    let actOverflow = false;
    if (actEl) {
      // clamp された状態で溢れているか判定 (描画後に実測)
      requestAnimationFrame(() => {
        actOverflow = actEl.scrollHeight > actEl.clientHeight + 1;
        if (actOverflow || hasDetailFields) expandBtn.style.display = '';
      });
    } else if (hasDetailFields) {
      expandBtn.style.display = '';
    }
  }

  function toggleExpand(it, card) {
    const detailEl = card.querySelector('.s2-card-detail');
    const actEl = card.querySelector('.s2-card-act');
    const expandBtn = card.querySelector('.s2-expand-btn');
    const isOpen = card.classList.toggle('is-expanded');

    if (isOpen) {
      if (actEl) actEl.classList.remove('is-clamped');
      if (!detailEl.dataset.built) {
        const rows = [];
        if (it.area)    rows.push(`<div class="s2-detail-row"><span class="s2-detail-label">エリア</span><span>${esc(it.area)}</span></div>`);
        if (it.contact) rows.push(`<div class="s2-detail-row"><span class="s2-detail-label">面会者</span><span>${esc(it.contact)}</span></div>`);
        if (it.note)    rows.push(`<div class="s2-detail-row"><span class="s2-detail-label">所感</span><span class="s2-detail-note">${esc(it.note)}</span></div>`);
        detailEl.innerHTML = rows.join('');
        detailEl.dataset.built = '1';
      }
      detailEl.style.display = detailEl.dataset.built && detailEl.innerHTML ? '' : 'none';
      expandBtn.textContent = '閉じる';
    } else {
      if (actEl) actEl.classList.add('is-clamped');
      detailEl.style.display = 'none';
      expandBtn.textContent = 'もっと見る';
    }
  }

  // ---------- いいね ----------

  async function toggleLike(it, card) {
    if (it.likePending) return;
    it.likePending = true;

    const likeBtn = card.querySelector('.s2-like-btn');
    const countEl = card.querySelector('.s2-like-count');
    const wasLiked = it.likedByMe;

    // 楽観的更新
    it.likedByMe = !wasLiked;
    it.likeCount += wasLiked ? -1 : 1;
    if (it.likeCount < 0) it.likeCount = 0;
    likeBtn.classList.toggle('is-liked', it.likedByMe);
    countEl.textContent = it.likeCount || '';

    try {
      const likeRef = doc(db, 'reports', it.id, 'likes', myMemberId);
      const reportRef = doc(db, 'reports', it.id);
      const batch = writeBatch(db);
      if (wasLiked) {
        batch.delete(likeRef);
        batch.update(reportRef, { likeCount: increment(-1) });
      } else {
        batch.set(likeRef, {
          memberId: myMemberId,
          memberName: currentMember.name || '',
          createdAt: serverTimestamp(),
        });
        batch.update(reportRef, { likeCount: increment(1) });
      }
      await batch.commit();
    } catch (e) {
      console.error('[S2] いいね失敗:', e);
      // ロールバック
      it.likedByMe = wasLiked;
      it.likeCount += wasLiked ? 1 : -1;
      if (it.likeCount < 0) it.likeCount = 0;
      likeBtn.classList.toggle('is-liked', it.likedByMe);
      countEl.textContent = it.likeCount || '';
      showToast('いいねに失敗しました', 'error');
    } finally {
      it.likePending = false;
    }
  }

  // ---------- メニュー / 削除 (Phase 2-B-3.3) ----------

  function closeAllMenus(except) {
    feedEl.querySelectorAll('.s2-card-menu.is-open').forEach((m) => {
      if (m !== except) m.classList.remove('is-open');
    });
  }

  function toggleMenu(card) {
    const menu = card.querySelector('.s2-card-menu');
    if (!menu) return;
    const willOpen = !menu.classList.contains('is-open');
    closeAllMenus();
    if (willOpen) menu.classList.add('is-open');
  }

  function onDocClick(ev) {
    // メニューボタン自体のクリックは toggleMenu 側で処理済み
    if (ev.target.closest('[data-action="menu"]')) return;
    closeAllMenus();
  }

  /** 日報削除: likes / comments サブコレクション全件 + 本体を writeBatch で連鎖削除 */
  async function deleteReport(it, card) {
    if (it.deletePending) return;
    closeAllMenus();

    const ok = window.confirm(
      `この日報を削除しますか?\n${it.clientText ? `顧客: ${it.clientText}\n` : ''}` +
      'ついている「いいね」とコメントもすべて削除されます。\nこの操作は取り消せません。'
    );
    if (!ok) return;

    it.deletePending = true;
    try {
      const [likesSnap, commentsSnap] = await Promise.all([
        getDocs(collection(db, 'reports', it.id, 'likes')),
        getDocs(collection(db, 'reports', it.id, 'comments')),
      ]);

      const batch = writeBatch(db);
      likesSnap.forEach((d) => batch.delete(d.ref));
      commentsSnap.forEach((d) => batch.delete(d.ref));
      batch.delete(doc(db, 'reports', it.id));
      await batch.commit();

      // DOM から除去 (日付グループが空になったらグループごと除去)
      itemMap.delete(it.id);
      const group = card.closest('.s2-date-group');
      card.remove();
      if (group && !group.querySelector('.s2-card')) group.remove();

      showToast('日報を削除しました', 'success');

      if (itemMap.size === 0) {
        // 表示中カードが無くなった場合は先頭から読み直す (続きがあれば表示される)
        resetAndLoad();
      }
    } catch (e) {
      console.error('[S2] 日報削除失敗:', e);
      showToast('日報の削除に失敗しました', 'error');
    } finally {
      it.deletePending = false;
    }
  }

  // ---------- コメント ----------

  async function toggleComments(it, card) {
    const wrap = card.querySelector('.s2-comments');
    const isHidden = wrap.style.display === 'none';
    wrap.style.display = isHidden ? '' : 'none';
    if (!isHidden) return;

    if (!it.commentsLoaded) {
      const listEl = wrap.querySelector('.s2-comments-list');
      listEl.innerHTML = `<div class="s2-loading s2-loading-sm">読み込み中…</div>`;
      try {
        const q = query(
          collection(db, 'reports', it.id, 'comments'),
          orderBy('createdAt', 'asc')
        );
        const snap = await getDocs(q);
        listEl.innerHTML = '';
        snap.forEach((d) => appendCommentRow(listEl, it, card, d.id, d.data()));
        if (snap.empty) listEl.innerHTML = `<div class="s2-comments-empty">最初のコメントを書いてみましょう</div>`;
        it.commentsLoaded = true;
      } catch (e) {
        console.error('[S2] コメント取得失敗:', e);
        listEl.innerHTML = `<div class="s2-comments-empty">コメントの読み込みに失敗しました</div>`;
      }
    }
    // 入力欄にフォーカス
    const input = wrap.querySelector('.s2-comment-input');
    if (input) input.focus();
  }

  function appendCommentRow(listEl, it, card, commentId, data) {
    const colorKey = (memberColorMap && memberColorMap[data.memberId]) || 'slate';
    const isMine = data.memberId === myMemberId;
    const row = document.createElement('div');
    row.className = 's2-comment-row';
    row.dataset.commentId = commentId;
    row.innerHTML = `
      ${avatarHTML(data.memberName, colorKey, 's2-avatar-sm')}
      <div class="s2-comment-body">
        <div class="s2-comment-meta">
          <span class="s2-comment-name">${esc(data.memberName)}</span>
          <span class="s2-comment-time">${esc(formatCommentTime(data.createdAt))}</span>
          <span class="s2-comment-edited" ${data.updatedAt ? '' : 'style="display:none;"'}>編集済み</span>
          ${isMine ? `
          <span class="s2-comment-actions">
            <button type="button" class="s2-comment-action" data-action="edit-comment">編集</button>
            <button type="button" class="s2-comment-action is-danger" data-action="delete-comment">削除</button>
          </span>` : ''}
        </div>
        <div class="s2-comment-text">${esc(data.text)}</div>
        ${isMine ? `
        <div class="s2-comment-edit" style="display:none;">
          <textarea class="s2-comment-edit-input" maxlength="500" rows="2"></textarea>
          <div class="s2-comment-edit-btns">
            <button type="button" class="s2-comment-edit-cancel" data-action="cancel-edit-comment">キャンセル</button>
            <button type="button" class="s2-comment-edit-save" data-action="save-edit-comment">保存</button>
          </div>
        </div>` : ''}
      </div>
    `;
    listEl.appendChild(row);
  }

  /** コメント編集モードの開閉 (本人のみ) */
  function setCommentEditing(row, editing) {
    if (!row) return;
    const textEl = row.querySelector('.s2-comment-text');
    const editEl = row.querySelector('.s2-comment-edit');
    const actionsEl = row.querySelector('.s2-comment-actions');
    if (!textEl || !editEl) return;
    if (editing) {
      const input = editEl.querySelector('.s2-comment-edit-input');
      input.value = textEl.textContent;
      textEl.style.display = 'none';
      if (actionsEl) actionsEl.style.display = 'none';
      editEl.style.display = '';
      input.focus();
      input.setSelectionRange(input.value.length, input.value.length);
    } else {
      textEl.style.display = '';
      if (actionsEl) actionsEl.style.display = '';
      editEl.style.display = 'none';
    }
  }

  /** コメント編集の保存: text / updatedAt のみ updateDoc (rules 側も同フィールドに限定) */
  async function saveCommentEdit(it, row) {
    if (!row || row.dataset.saving === '1') return;
    const textEl = row.querySelector('.s2-comment-text');
    const input = row.querySelector('.s2-comment-edit-input');
    const saveBtn = row.querySelector('.s2-comment-edit-save');
    const text = (input.value || '').trim();
    if (!text) {
      showToast('コメントを入力してください', 'error');
      return;
    }
    if (text.length > 500) {
      showToast('コメントは500文字以内で入力してください', 'error');
      return;
    }
    if (text === textEl.textContent) {
      setCommentEditing(row, false);
      return;
    }

    row.dataset.saving = '1';
    saveBtn.disabled = true;
    try {
      await updateDoc(doc(db, 'reports', it.id, 'comments', row.dataset.commentId), {
        text,
        updatedAt: serverTimestamp(),
      });
      textEl.textContent = text;
      const editedEl = row.querySelector('.s2-comment-edited');
      if (editedEl) editedEl.style.display = '';
      setCommentEditing(row, false);
    } catch (e) {
      console.error('[S2] コメント編集失敗:', e);
      showToast('コメントの編集に失敗しました', 'error');
    } finally {
      row.dataset.saving = '';
      saveBtn.disabled = false;
    }
  }

  /** コメント削除 (本人のみ): コメント削除 + 親 commentCount decrement を writeBatch で */
  async function deleteComment(it, card, row) {
    if (!row || row.dataset.deleting === '1') return;
    if (!window.confirm('このコメントを削除しますか?\nこの操作は取り消せません。')) return;

    row.dataset.deleting = '1';
    try {
      const batch = writeBatch(db);
      batch.delete(doc(db, 'reports', it.id, 'comments', row.dataset.commentId));
      batch.update(doc(db, 'reports', it.id), { commentCount: increment(-1) });
      await batch.commit();

      const listEl = row.parentElement;
      row.remove();
      if (listEl && !listEl.querySelector('.s2-comment-row')) {
        listEl.innerHTML = `<div class="s2-comments-empty">最初のコメントを書いてみましょう</div>`;
      }
      it.commentCount = Math.max(0, (it.commentCount || 0) - 1);
      card.querySelector('.s2-comment-count').textContent = it.commentCount || '';
    } catch (e) {
      console.error('[S2] コメント削除失敗:', e);
      showToast('コメントの削除に失敗しました', 'error');
      row.dataset.deleting = '';
    }
  }

  async function sendComment(it, card) {
    const wrap = card.querySelector('.s2-comments');
    const input = wrap.querySelector('.s2-comment-input');
    const sendBtn = wrap.querySelector('.s2-comment-send');
    const listEl = wrap.querySelector('.s2-comments-list');
    const text = (input.value || '').trim();
    if (!text) return;
    if (text.length > 500) {
      showToast('コメントは500文字以内で入力してください', 'error');
      return;
    }

    sendBtn.disabled = true;
    try {
      const batch = writeBatch(db);
      const commentRef = doc(collection(db, 'reports', it.id, 'comments'));
      batch.set(commentRef, {
        memberId: myMemberId,
        memberName: currentMember.name || '',
        text,
        createdAt: serverTimestamp(),
      });
      batch.update(doc(db, 'reports', it.id), { commentCount: increment(1) });
      await batch.commit();

      // 楽観的にローカル追加
      const emptyEl = listEl.querySelector('.s2-comments-empty');
      if (emptyEl) emptyEl.remove();
      appendCommentRow(listEl, it, card, commentRef.id, {
        memberId: myMemberId,
        memberName: currentMember.name || '',
        text,
        createdAt: Timestamp.now(),
      });
      it.commentCount += 1;
      card.querySelector('.s2-comment-count').textContent = it.commentCount;
      input.value = '';
    } catch (e) {
      console.error('[S2] コメント送信失敗:', e);
      showToast('コメントの送信に失敗しました', 'error');
    } finally {
      sendBtn.disabled = false;
    }
  }

  // ---------- イベント委譲 ----------

  function onFeedClick(ev) {
    const btn = ev.target.closest('[data-action]');
    if (!btn) return;
    const card = btn.closest('.s2-card');
    if (!card) return;
    const it = itemMap.get(card.dataset.reportId);
    if (!it) return;

    const action = btn.dataset.action;
    if (action === 'expand') toggleExpand(it, card);
    else if (action === 'like') toggleLike(it, card);
    else if (action === 'comment') toggleComments(it, card);
    else if (action === 'send-comment') sendComment(it, card);
    else if (action === 'edit-comment') setCommentEditing(btn.closest('.s2-comment-row'), true);
    else if (action === 'cancel-edit-comment') setCommentEditing(btn.closest('.s2-comment-row'), false);
    else if (action === 'save-edit-comment') saveCommentEdit(it, btn.closest('.s2-comment-row'));
    else if (action === 'delete-comment') deleteComment(it, card, btn.closest('.s2-comment-row'));
    else if (action === 'menu') toggleMenu(card);
    else if (action === 'edit') {
      closeAllMenus();
      if (onEditReport) onEditReport(it);
    }
    else if (action === 'delete') deleteReport(it, card);
  }

  function onFeedKeydown(ev) {
    // コメント編集中: Esc でキャンセル / Enter (Shift なし・IME 確定中でない) で保存
    const editInput = ev.target.closest('.s2-comment-edit-input');
    if (editInput) {
      const row = editInput.closest('.s2-comment-row');
      if (ev.key === 'Escape') {
        ev.preventDefault();
        setCommentEditing(row, false);
      } else if (ev.key === 'Enter' && !ev.shiftKey && !ev.isComposing) {
        ev.preventDefault();
        const card = row.closest('.s2-card');
        const it = card && itemMap.get(card.dataset.reportId);
        if (it) saveCommentEdit(it, row);
      }
      return;
    }
    if (ev.key !== 'Enter') return;
    const input = ev.target.closest('.s2-comment-input');
    if (!input) return;
    ev.preventDefault();
    const card = input.closest('.s2-card');
    const it = card && itemMap.get(card.dataset.reportId);
    if (it) sendComment(it, card);
  }

  feedEl.addEventListener('click', onFeedClick);
  feedEl.addEventListener('keydown', onFeedKeydown);
  document.addEventListener('click', onDocClick);
  if (autoLoad) rootEl.addEventListener('scroll', onTimelineScroll, { passive: true });

  // ---------- 初期ロード ----------

  loadNextPage();

  // ---------- controller ----------

  return {
    destroy() {
      destroyed = true;
      feedEl.removeEventListener('click', onFeedClick);
      feedEl.removeEventListener('keydown', onFeedKeydown);
      document.removeEventListener('click', onDocClick);
      if (autoLoad) rootEl.removeEventListener('scroll', onTimelineScroll);
    },
    /**
     * タブ再表示時に呼ばれる
     * @param {Object} o { reload: boolean } 新着がある場合や日報保存直後は true
     */
    onShow(o = {}) {
      if (o.reload) resetAndLoad();
    },
  };
}
